/**
 * Container Command Implementations for UI-Model-1 (API-Direct Approach)
 *
 * Execute and undo functions for container operations.
 * Operates directly on the nested API structure (canvas.containers[]).
 */

import {
  UIModelState,
  ContainerAPI,
  DEFAULT_CONTAINER_CONFIGURATION,
  DirtySection,
} from '../../types/state.types';
import {
  AddContainerCommand,
  RemoveContainerCommand,
  UpdateContainerCommand,
  MoveContainerCommand,
  ReorderContainersCommand,
  DuplicateContainerCommand,
} from '../../types/command.types';
import { generateId } from '../../utils/idGenerator';

// ============================================================================
// Helper Functions for Dirty Tracking
// ============================================================================

/**
 * Create updated dirty tracker with new dirty sections
 */
const markCanvasDirty = (
  dirtyTracker: UIModelState['dirtyTracker'],
  canvasId: string
): UIModelState['dirtyTracker'] => {
  const newSections = new Set(Array.from(dirtyTracker.sections));
  newSections.add('canvas' as DirtySection);

  const newCanvasPages = new Set(Array.from(dirtyTracker.canvasPages));
  newCanvasPages.add(canvasId);

  return {
    ...dirtyTracker,
    sections: newSections,
    canvasPages: newCanvasPages,
  };
};

// ============================================================================
// Helper Functions for Nested Structure Manipulation
// ============================================================================

/**
 * Deep clone the model to avoid mutations
 */
const cloneModel = (
  model: UIModelState['model']
): UIModelState['model'] => {
  if (!model) return null;
  return JSON.parse(JSON.stringify(model));
};

/**
 * Find and update a container in nested structure
 * Returns a new array with the updated container
 */
const updateContainerInList = (
  containers: ContainerAPI[],
  containerId: string,
  updater: (container: ContainerAPI) => ContainerAPI
): ContainerAPI[] => {
  return containers.map(container => {
    if (container.id === containerId) {
      return updater(container);
    }

    // Recurse into nested containers
    if (container.content.nestedContainers) {
      return {
        ...container,
        content: {
          ...container.content,
          nestedContainers: updateContainerInList(
            container.content.nestedContainers,
            containerId,
            updater
          ),
        },
      };
    }

    return container;
  });
};

/**
 * Remove a container from nested structure
 * Returns [newContainers, removedContainer]
 */
const removeContainerFromList = (
  containers: ContainerAPI[],
  containerId: string
): [ContainerAPI[], ContainerAPI | undefined] => {
  let removedContainer: ContainerAPI | undefined;

  const newContainers = containers.filter(container => {
    if (container.id === containerId) {
      removedContainer = container;
      return false;
    }
    return true;
  });

  // If not found at this level, check nested containers
  if (!removedContainer) {
    return [
      containers.map(container => {
        if (container.content.nestedContainers) {
          const [newNested, removed] = removeContainerFromList(
            container.content.nestedContainers,
            containerId
          );
          if (removed) {
            removedContainer = removed;
            return {
              ...container,
              content: {
                ...container.content,
                nestedContainers: newNested,
              },
            };
          }
        }
        return container;
      }),
      removedContainer,
    ];
  }

  return [newContainers, removedContainer];
};

/**
 * Add a container to nested structure at specified parent
 */
const addContainerToList = (
  containers: ContainerAPI[],
  newContainer: ContainerAPI,
  parentContainerId: string | undefined,
  insertIndex: number | undefined
): ContainerAPI[] => {
  // If no parent, add to top level
  if (!parentContainerId) {
    const newContainers = [...containers];
    const index = insertIndex ?? newContainers.length;
    newContainers.splice(index, 0, newContainer);
    return newContainers;
  }

  // Find parent and add as nested container
  return containers.map(container => {
    if (container.id === parentContainerId) {
      const nestedContainers = container.content.nestedContainers || [];
      const newNested = [...nestedContainers];
      const index = insertIndex ?? newNested.length;
      newNested.splice(index, 0, newContainer);
      return {
        ...container,
        content: {
          ...container.content,
          nestedContainers: newNested,
        },
      };
    }

    // Recurse into nested containers
    if (container.content.nestedContainers) {
      return {
        ...container,
        content: {
          ...container.content,
          nestedContainers: addContainerToList(
            container.content.nestedContainers,
            newContainer,
            parentContainerId,
            insertIndex
          ),
        },
      };
    }

    return container;
  });
};

// ============================================================================
// Add Container
// ============================================================================

export const executeAddContainer = (
  state: UIModelState,
  command: AddContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { container, canvasId, insertIndex, parentContainerId } = command.payload;

  // Create container ID if not provided
  const containerId = container.id || generateId('container');
  const containerOrder = insertIndex ?? container.order ?? 0;

  const newContainer: ContainerAPI = {
    id: containerId,
    name: container.name || 'New Container',
    order: containerOrder,
    rowIndex: container.rowIndex ?? container.metadata?.rowIndex ?? 0,
    configuration: container.configuration || DEFAULT_CONTAINER_CONFIGURATION,
    content: container.content || { items: [] },
    usageInfo: container.usageInfo || { usedComponents: [] },
    metadata: {
      type: parentContainerId ? 'nested-container' : 'container',
      isNested: !!parentContainerId,
      parentContainerId,
      rowIndex: container.rowIndex ?? container.metadata?.rowIndex ?? 0,
      nestingLevel: parentContainerId ? 1 : 0,
    },
  };

  // Clone the model to avoid mutations
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get or auto-create the canvas (recovers from missing canvas data)
  let canvas = newModel.layout.canvas[canvasId];
  if (!canvas) {
    const activePageId = state.editorState.activePageId;
    const activeGroupId = state.editorState.activeGroupId;
    canvas = { pageId: activePageId || '', groupId: activeGroupId || '', containers: [] };
    newModel.layout.canvas[canvasId] = canvas;
  }

  // Add container to canvas
  canvas.containers = addContainerToList(
    canvas.containers,
    newContainer,
    parentContainerId,
    insertIndex
  );

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoAddContainer = (
  state: UIModelState,
  command: AddContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId } = command.payload;
  const containerId = command.payload.container.id;
  if (!containerId) return state;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  // Remove the container
  const [newContainers] = removeContainerFromList(canvas.containers, containerId);
  canvas.containers = newContainers;

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

// ============================================================================
// Remove Container
// ============================================================================

export const executeRemoveContainer = (
  state: UIModelState,
  command: RemoveContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { containerId, canvasId } = command.payload;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  // Remove the container and store for undo
  const [newContainers, removedContainer] = removeContainerFromList(canvas.containers, containerId);

  if (!removedContainer) return state;

  canvas.containers = newContainers;

  // Store removed container in command for undo
  command.payload.removedContainer = removedContainer;

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoRemoveContainer = (
  state: UIModelState,
  command: RemoveContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId, removedContainer, parentContainerId } = command.payload;

  if (!removedContainer) return state;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  // Add the container back
  canvas.containers = addContainerToList(
    canvas.containers,
    removedContainer,
    parentContainerId,
    removedContainer.order
  );

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

// ============================================================================
// Update Container
// ============================================================================

export const executeUpdateContainer = (
  state: UIModelState,
  command: UpdateContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { containerId, canvasId, updates } = command.payload;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  // Store previous values for undo
  let previousValues: Partial<ContainerAPI> | undefined;

  // Update the container
  canvas.containers = updateContainerInList(canvas.containers, containerId, container => {
    // Store previous values
    previousValues = {
      name: container.name,
      order: container.order,
      configuration: { ...container.configuration },
      content: { ...container.content },
      rowIndex: container.rowIndex,
      metadata: container.metadata ? { ...container.metadata } : undefined,
    };

    // Build updated container
    const updated: ContainerAPI = {
      ...container,
    };

    if (updates.name !== undefined) {
      updated.name = updates.name;
    }

    if (updates.configuration) {
      updated.configuration = { ...container.configuration, ...updates.configuration };
    }

    if (updates.submodel !== undefined) {
      updated.content = {
        ...updated.content,
        submodel: updates.submodel || undefined,
      };
    }

    if (updates.submodels !== undefined) {
      updated.content = {
        ...updated.content,
        submodels: updates.submodels || undefined,
      };
    }

    // Handle items updates
    if (updates.items !== undefined) {
      updated.content = {
        ...updated.content,
        items: updates.items,
      };
    }

    if (updates.order !== undefined) {
      updated.order = updates.order;
    }

    if (updates.rowIndex !== undefined) {
      updated.rowIndex = updates.rowIndex;
    }

    if (updates.metadata !== undefined) {
      updated.metadata = container.metadata
        ? { ...container.metadata, ...updates.metadata }
        : updates.metadata as ContainerAPI['metadata'];
    }

    return updated;
  });

  command.payload.previousValues = previousValues;

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoUpdateContainer = (
  state: UIModelState,
  command: UpdateContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { containerId, canvasId, previousValues } = command.payload;

  if (!previousValues) return state;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  // Restore previous values
  canvas.containers = updateContainerInList(canvas.containers, containerId, container => ({
    ...container,
    name: previousValues.name ?? container.name,
    order: previousValues.order ?? container.order,
    configuration: previousValues.configuration
      ? { ...container.configuration, ...previousValues.configuration }
      : container.configuration,
    content: previousValues.content
      ? { ...container.content, ...previousValues.content }
      : container.content,
    rowIndex: previousValues.rowIndex ?? container.rowIndex,
    metadata: previousValues.metadata
      ? { ...container.metadata, ...previousValues.metadata }
      : container.metadata,
  }));

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

// ============================================================================
// Move Container
// ============================================================================

export const executeMoveContainer = (
  state: UIModelState,
  command: MoveContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const {
    containerId,
    fromCanvasId,
    toCanvasId,
    toIndex,
    toParentId,
  } = command.payload;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get source canvas
  const fromCanvas = newModel.layout.canvas[fromCanvasId];
  if (!fromCanvas) return state;

  // Remove from source
  const [newFromContainers, movedContainer] = removeContainerFromList(
    fromCanvas.containers,
    containerId
  );

  if (!movedContainer) return state;

  fromCanvas.containers = newFromContainers;

  // Update container metadata for new location
  const updatedContainer: ContainerAPI = {
    ...movedContainer,
    order: toIndex,
    metadata: {
      ...movedContainer.metadata,
      type: toParentId ? 'nested-container' : 'container',
      isNested: !!toParentId,
      parentContainerId: toParentId || undefined,
    },
  };

  // Add to target canvas
  const toCanvas = newModel.layout.canvas[toCanvasId];
  if (!toCanvas) return state;

  toCanvas.containers = addContainerToList(
    toCanvas.containers,
    updatedContainer,
    toParentId || undefined,
    toIndex
  );

  // Update dirty tracking for both canvases
  let newDirtyTracker = markCanvasDirty(state.dirtyTracker, fromCanvasId);
  if (fromCanvasId !== toCanvasId) {
    newDirtyTracker = markCanvasDirty(newDirtyTracker, toCanvasId);
  }

  return {
    ...state,
    model: newModel,
    dirtyTracker: newDirtyTracker,
  };
};

export const undoMoveContainer = (
  state: UIModelState,
  command: MoveContainerCommand
): UIModelState => {
  // Reverse the move by swapping from/to
  const reverseCommand: MoveContainerCommand = {
    ...command,
    payload: {
      containerId: command.payload.containerId,
      fromCanvasId: command.payload.toCanvasId,
      toCanvasId: command.payload.fromCanvasId,
      fromIndex: command.payload.toIndex,
      toIndex: command.payload.fromIndex,
      fromParentId: command.payload.toParentId,
      toParentId: command.payload.fromParentId,
    },
  };

  return executeMoveContainer(state, reverseCommand);
};

// ============================================================================
// Reorder Containers
// ============================================================================

export const executeReorderContainers = (
  state: UIModelState,
  command: ReorderContainersCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId, parentId, containerIds } = command.payload;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  if (parentId) {
    // Reorder nested containers within a parent
    canvas.containers = updateContainerInList(canvas.containers, parentId, parent => {
      if (!parent.content.nestedContainers) return parent;

      // Store previous order for undo
      command.payload.previousOrder = parent.content.nestedContainers.map(c => c.id);

      // Create new order based on containerIds
      const containerMap = new Map(
        parent.content.nestedContainers.map(c => [c.id, c])
      );
      const reorderedContainers = containerIds
        .map(id => containerMap.get(id))
        .filter((c): c is ContainerAPI => c !== undefined)
        .map((c, index) => ({ ...c, order: index }));

      return {
        ...parent,
        content: {
          ...parent.content,
          nestedContainers: reorderedContainers,
        },
      };
    });
  } else {
    // Reorder top-level containers
    command.payload.previousOrder = canvas.containers.map(c => c.id);

    const containerMap = new Map(canvas.containers.map(c => [c.id, c]));
    canvas.containers = containerIds
      .map(id => containerMap.get(id))
      .filter((c): c is ContainerAPI => c !== undefined)
      .map((c, index) => ({ ...c, order: index }));
  }

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoReorderContainers = (
  state: UIModelState,
  command: ReorderContainersCommand
): UIModelState => {
  const { previousOrder } = command.payload;

  if (!previousOrder) return state;

  // Reorder back to previous order
  const reverseCommand: ReorderContainersCommand = {
    ...command,
    payload: {
      ...command.payload,
      containerIds: previousOrder,
      previousOrder: command.payload.containerIds,
    },
  };

  return executeReorderContainers(state, reverseCommand);
};

// ============================================================================
// Duplicate Container
// ============================================================================

export const executeDuplicateContainer = (
  state: UIModelState,
  command: DuplicateContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId, duplicatedContainer, parentContainerId } = command.payload;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  // Add the duplicated container (insert after the source container's order)
  canvas.containers = addContainerToList(
    canvas.containers,
    duplicatedContainer,
    parentContainerId,
    duplicatedContainer.order
  );

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};

export const undoDuplicateContainer = (
  state: UIModelState,
  command: DuplicateContainerCommand
): UIModelState => {
  if (!state.model) return state;

  const { canvasId, duplicatedContainerId } = command.payload;

  // Clone the model
  const newModel = cloneModel(state.model);
  if (!newModel) return state;

  // Get the canvas
  const canvas = newModel.layout.canvas[canvasId];
  if (!canvas) return state;

  // Remove the duplicated container
  const [newContainers] = removeContainerFromList(canvas.containers, duplicatedContainerId);
  canvas.containers = newContainers;

  return {
    ...state,
    model: newModel,
    dirtyTracker: markCanvasDirty(state.dirtyTracker, canvasId),
  };
};
